import { useMemo, useState } from "react";
import "../../styles/design-system.css";
import "../features.css";
import { Tag } from "../../components/ui/Tag";
import { Badge } from "../../components/ui/Badge";
import { track } from "../../utils/analytics";
import { useFeatureView } from "../../utils/useFeatureView";

const QUESTIONS = [
  {
    id: "housing",
    domain: "Housing",
    text: "What is your current housing situation?",
    options: [
      { value: "stable",   label: "Stable housing",             risk: 0 },
      { value: "doubled",  label: "Doubled up / temporary",     risk: 1 },
      { value: "unstable", label: "Unstable / unsafe",          risk: 2 },
      { value: "homeless", label: "No regular place to stay",   risk: 2 },
    ],
  },
  {
    id: "food_worry",
    domain: "Food",
    text: "Within the past 12 months, you worried food would run out before you got money to buy more.",
    options: [
      { value: "never",     label: "Never true",     risk: 0 },
      { value: "sometimes", label: "Sometimes true", risk: 1 },
      { value: "often",     label: "Often true",     risk: 2 },
    ],
  },
  {
    id: "food_ran_out",
    domain: "Food",
    text: "Within the past 12 months, the food you bought didn't last and you didn't have money to get more.",
    options: [
      { value: "never",     label: "Never true",     risk: 0 },
      { value: "sometimes", label: "Sometimes true", risk: 1 },
      { value: "often",     label: "Often true",     risk: 2 },
    ],
  },
  {
    id: "transport",
    domain: "Transportation",
    text: "In the past 12 months, has lack of transportation kept you from medical appointments?",
    options: [
      { value: "no",  label: "No",  risk: 0 },
      { value: "yes", label: "Yes", risk: 2 },
    ],
  },
  {
    id: "utilities",
    domain: "Utilities",
    text: "In the past 12 months, has the electric, gas, or water company threatened to shut off services?",
    options: [
      { value: "no",  label: "No",  risk: 0 },
      { value: "yes", label: "Yes", risk: 2 },
    ],
  },
  {
    id: "safety",
    domain: "Safety",
    text: "Do you feel physically and emotionally safe where you currently live?",
    options: [
      { value: "yes",    label: "Yes",    risk: 0 },
      { value: "unsure", label: "Unsure", risk: 1 },
      { value: "no",     label: "No",     risk: 2 },
    ],
  },
  {
    id: "employment",
    domain: "Employment",
    text: "What is your current work situation?",
    options: [
      { value: "ft",    label: "Full-time",              risk: 0 },
      { value: "pt",    label: "Part-time / seasonal",   risk: 1 },
      { value: "unemp", label: "Unemployed / unable to work", risk: 2 },
      { value: "ret",   label: "Retired",                risk: 0 },
    ],
  },
  {
    id: "language",
    domain: "Access",
    text: "What language do you feel most comfortable speaking with your care team?",
    options: [
      { value: "english", label: "English", risk: 0 },
      { value: "spanish", label: "Spanish", risk: 1 },
      { value: "other",   label: "Other",   risk: 1 },
    ],
  },
];

// Wizard steps — one domain per screen, Food groups its 2 questions together
const STEPS = [
  {
    id: "housing",
    domain: "Housing stability",
    frame: "First, a few questions about your current living situation.",
    qids: ["housing"],
  },
  {
    id: "food",
    domain: "Food access",
    frame: "Now a few questions about food and nutrition access.",
    qids: ["food_worry", "food_ran_out"],
  },
  {
    id: "transport",
    domain: "Transportation",
    frame: "Getting to appointments and the pharmacy can be a barrier to care.",
    qids: ["transport"],
  },
  {
    id: "utilities",
    domain: "Utilities & bills",
    frame: "Managing household bills is part of health stability.",
    qids: ["utilities"],
  },
  {
    id: "safety",
    domain: "Personal safety",
    frame: "Feeling safe at home matters for overall health.",
    qids: ["safety"],
  },
  {
    id: "employment",
    domain: "Work & income",
    frame: "Your work situation affects access to care and insurance coverage.",
    qids: ["employment"],
  },
  {
    id: "language",
    domain: "Language & communication",
    frame: "We want to make sure we can support you in your preferred language.",
    qids: ["language"],
  },
];

// Maria T. demo patient answers (matches her GTH-8821 profile in trial matching)
const MARIA_T_ANSWERS = {
  housing:      "stable",
  food_worry:   "often",
  food_ran_out: "sometimes",
  transport:    "yes",
  utilities:    "no",
  safety:       "yes",
  employment:   "pt",
  language:     "spanish",
};

function scoreAnswers(answers) {
  const byDomain = {};
  let total = 0;
  let answered = 0;
  for (const q of QUESTIONS) {
    const a = answers[q.id];
    if (!a) continue;
    answered += 1;
    const opt = q.options.find((o) => o.value === a);
    const risk = opt ? opt.risk : 0;
    total += risk;
    byDomain[q.domain] = (byDomain[q.domain] ?? 0) + risk;
  }
  const tier = total >= 7 ? "high" : total >= 3 ? "moderate" : "low";
  return { byDomain, total, answered, tier };
}

function recommendedReferrals(answers) {
  const refs = [];
  if (answers.food_worry === "often" || answers.food_worry === "sometimes" ||
      answers.food_ran_out === "often" || answers.food_ran_out === "sometimes") {
    refs.push("Food-as-medicine: Produce Rx, SNAP/WIC navigation");
  }
  if (answers.transport === "yes") {
    refs.push("Transportation: rideshare voucher, CHW home visit");
  }
  if (answers.utilities === "yes") {
    refs.push("LIHEAP / utility assistance application support");
  }
  if (answers.housing && answers.housing !== "stable") {
    refs.push("Housing navigation: shelter, voucher, legal aid");
  }
  if (answers.safety === "no" || answers.safety === "unsure") {
    refs.push("Safety: confidential SW consult, IPV resources");
  }
  if (answers.language && answers.language !== "english") {
    refs.push("Language access: certified interpreter at every visit");
  }
  return refs;
}

export function SDOHScreening() {
  const sectionRef = useFeatureView("sdoh_screening");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [demoPatient, setDemoPatient] = useState(null);

  const result = useMemo(() => scoreAnswers(answers), [answers]);
  const refs = useMemo(() => recommendedReferrals(answers), [answers]);

  const currentStep = STEPS[step];
  const currentQuestions = QUESTIONS.filter((q) => currentStep.qids.includes(q.id));
  const canAdvance = currentQuestions.every((q) => answers[q.id] !== undefined);
  const isLastStep = step === STEPS.length - 1;

  const tierLabel =
    result.tier === "high" ? "High SDOH burden" :
    result.tier === "moderate" ? "Moderate SDOH burden" : "Low SDOH burden";

  function setAnswer(qid, value) {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  }

  function goNext() {
    if (!isLastStep) {
      setStep((s) => s + 1);
      track("sdoh_step_next", { step, domain: currentStep.domain });
    } else {
      setSubmitted(true);
      track("sdoh_screening_complete", {
        tier: result.tier,
        score: result.total,
        referralCount: refs.length,
      });
    }
  }

  function goBack() {
    if (submitted) {
      setSubmitted(false);
    } else if (step > 0) {
      setStep((s) => s - 1);
    }
  }

  function loadDemoPatient() {
    setAnswers(MARIA_T_ANSWERS);
    setDemoPatient("maria-t");
    setSubmitted(true);
    track("sdoh_demo_patient_loaded", { patient: "maria-t" });
  }

  function reset() {
    setStep(0);
    setAnswers({});
    setSubmitted(false);
    setDemoPatient(null);
  }

  const progressPct = submitted ? 100 : ((step) / STEPS.length) * 100;

  // ── Results view ──
  if (submitted) {
    return (
      <section ref={sectionRef} className="ds-section feature-section" id="feature-sdoh">
        <div className="ds-page">
          <div className="ds-section-head">
            <div className="ds-section-num">02 / 05</div>
            <div>
              <h2 className="ds-section-title">SDOH screening</h2>
              <p className="ds-section-sub">
                PRAPARE and AHC-HRSN domains embedded directly in trial
                eligibility workflows. Risk tiers gate trial holds and trigger referrals.
              </p>
            </div>
          </div>

          {demoPatient === "maria-t" && (
            <div className="tm-journey-strip">
              <span className="tm-journey-patient">Maria T. · GTH-8821 · Mt. Auburn FQHC</span>
              <span className="tm-journey-sep">·</span>
              <span className="tm-journey-step tm-journey-step--done">01 Trial matching</span>
              <span className="tm-journey-sep">→</span>
              <span className="tm-journey-step tm-journey-step--current">02 SDOH screening</span>
              <span className="tm-journey-sep">→</span>
              <span className="tm-journey-step">03 Food referrals</span>
            </div>
          )}

          <div className="sdoh-wizard-progress feature-spaced">
            <div className="sdoh-wizard-progress-bar">
              <div className="sdoh-wizard-progress-fill" style={{ width: "100%" }} />
            </div>
            <span className="sdoh-wizard-progress-label">Complete · {QUESTIONS.length} items</span>
          </div>

          <div className="ds-grid-2 feature-spaced">
            <div className="ds-card">
              <div className="ds-card-label">
                <span>RISK TIER</span>
                <span>SCORE {result.total}</span>
              </div>
              <div className="feature-tier">
                <Badge
                  status={result.tier === "high" ? "flag" : result.tier === "moderate" ? "draft" : "live"}
                >
                  {tierLabel}
                </Badge>
              </div>
              <div className="feature-domain-list feature-spaced">
                {Object.entries(result.byDomain).map(([d, s]) => (
                  <div key={d} className="feature-domain-row">
                    <span className="feature-domain-name">{d}</span>
                    <div className="feature-domain-bar">
                      <div className="feature-domain-fill" style={{ width: `${Math.min(100, s * 25)}%` }} />
                    </div>
                    <span className="feature-domain-score">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="ds-card">
                <div className="ds-card-label">
                  <span>RECOMMENDED REFERRALS</span>
                  <span>{refs.length}</span>
                </div>
                {refs.length === 0 ? (
                  <p className="feature-help">No referrals indicated.</p>
                ) : (
                  <ul className="feature-referrals">
                    {refs.map((r) => (
                      <li key={r}>
                        <span className="feature-bullet">/</span> {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="ds-card feature-spaced">
                <div className="ds-card-label">
                  <span>TRIAL ELIGIBILITY IMPACT</span>
                  <span>AUTO-GATING</span>
                </div>
                <p className="feature-help">
                  {result.tier === "high"
                    ? "High-burden patients are flagged for CHW outreach before randomization. Trials requiring food-insecurity inclusion (FOOD-Rx) become available."
                    : result.tier === "moderate"
                    ? "Patient remains eligible. Wraparound referrals are bundled with study consent."
                    : "Standard pathway. SDOH flags will not gate enrollment."}
                </p>
              </div>
            </div>
          </div>

          <div className="feature-actions feature-spaced">
            <button type="button" className="ds-btn ds-btn--ghost" onClick={goBack}>
              ← Edit answers
            </button>
            <button type="button" className="ds-btn ds-btn--ghost" onClick={reset}>
              Start over
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ── Wizard view ──
  return (
    <section ref={sectionRef} className="ds-section feature-section" id="feature-sdoh">
      <div className="ds-page">
        <div className="ds-section-head">
          <div className="ds-section-num">02 / 05</div>
          <div>
            <h2 className="ds-section-title">SDOH screening</h2>
            <p className="ds-section-sub">
              PRAPARE and AHC-HRSN domains embedded directly in trial
              eligibility workflows. Risk tiers gate trial holds and trigger referrals.
            </p>
          </div>
        </div>

        {/* Demo patient loader */}
        <div className="sdoh-demo-loader">
          <button type="button" className="sdoh-demo-btn" onClick={loadDemoPatient}>
            / Load Maria T. — demo patient
          </button>
          <span className="sdoh-demo-hint">
            Pre-fills all 7 domains with Maria T.'s PRAPARE answers · GTH-8821
          </span>
        </div>

        {/* Progress bar */}
        <div className="sdoh-wizard-progress feature-spaced">
          <div
            className="sdoh-wizard-progress-bar"
            role="progressbar"
            aria-valuenow={step + 1}
            aria-valuemin={1}
            aria-valuemax={STEPS.length}
            aria-label={`Step ${step + 1} of ${STEPS.length}: ${currentStep.domain}`}
          >
            <div className="sdoh-wizard-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="sdoh-wizard-progress-label" aria-live="polite">
            {step + 1} / {STEPS.length} domains · {currentStep.domain}
          </span>
        </div>

        {/* Step breadcrumbs */}
        <div className="sdoh-step-pills feature-spaced">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className={`sdoh-step-pill${i < step ? " sdoh-step-pill--done" : i === step ? " sdoh-step-pill--current" : ""}`}
              onClick={() => i < step && setStep(i)}
              disabled={i > step}
            >
              {i < step ? "✓" : i + 1} {s.domain}
            </button>
          ))}
        </div>

        {/* Current step card */}
        <div className="sdoh-wizard-card ds-card feature-spaced">
          <div className="sdoh-wizard-step-header">
            <Tag>{currentStep.domain}</Tag>
            <span className="sdoh-wizard-step-counter">{step + 1} of {STEPS.length}</span>
          </div>
          <p className="sdoh-wizard-frame">{currentStep.frame}</p>

          <ol className="feature-questions">
            {currentQuestions.map((q) => (
              <li key={q.id}>
                <div className="feature-question-text">{q.text}</div>
                <div className="feature-option-row">
                  {q.options.map((o) => {
                    const active = answers[q.id] === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        className={`feature-pill${active ? " feature-pill--active" : ""}`}
                        onClick={() => setAnswer(q.id, o.value)}
                        aria-pressed={active}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ol>

          <div className="sdoh-wizard-nav">
            {step > 0 && (
              <button type="button" className="ds-btn ds-btn--ghost" onClick={goBack}>
                ← Back
              </button>
            )}
            {!canAdvance && (
              <span className="sdoh-next-hint" aria-live="polite">
                Answer all questions above to continue
              </span>
            )}
            <button
              type="button"
              className="ds-btn ds-btn--slash"
              onClick={goNext}
              disabled={!canAdvance}
              aria-disabled={!canAdvance}
            >
              {isLastStep ? "Review results →" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SDOHScreening;
